package wat.ui

import kotlinx.browser.document
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import org.w3c.dom.events.Event
import wat.Supa
import wat.i18n.I18n.t

object AuthScreen {
    private var mode = "login" // login, signup, forgot
    private var email = ""
    private var password = ""
    private var password2 = ""
    private var error = ""
    private var success = ""
    private var loading = false
    var onAuth: ((dynamic) -> Unit)? = null

    private val INPUT_STYLE get() = if (ThemeManager.isDark()) {
        "width:100%;padding:14px 16px;border-radius:12px;border:1.5px solid ${Styles.CARD_BORDER};background:rgba(255,255,255,0.04);color:#fff;font-size:15px;font-weight:600;font-family:inherit;outline:none;box-sizing:border-box;transition:border-color 0.2s"
    } else {
        "width:100%;padding:14px 16px;border-radius:12px;border:1.5px solid ${Styles.CARD_BORDER};background:#f5f5fa;color:#1a1a2e;font-size:15px;font-weight:600;font-family:inherit;outline:none;box-sizing:border-box;transition:border-color 0.2s"
    }

    private val LABEL_STYLE get() = "font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:${Styles.TEXT_30};display:block;margin-bottom:6px"

    fun render(): HTMLElement {
        val container = div {
            style.cssText = "min-height:100vh;background:${Styles.BG_GRAD};color:${Styles.TEXT};font-family:'Space Grotesk',system-ui,sans-serif;display:flex;align-items:center;justify-content:center;padding:20px"
        }
        val wrapper = div { style.cssText = "width:100%;max-width:380px" }

        // Logo
        val authLogoGrad = if (ThemeManager.isDark()) "linear-gradient(135deg,#e2e8f0,#94a3b8)" else "linear-gradient(135deg,#3a3a4a,#5a5a6a)"
        val authLineGrad = if (ThemeManager.isDark()) "linear-gradient(90deg,#e2e8f0,#64748b)" else "linear-gradient(90deg,#3a3a4a,#5a5a6a)"
        wrapper.appendChild(div {
            style.cssText = "margin-bottom:24px;text-align:center"
            appendChild(span("TRACTOR") {
                style.cssText = "font-family:'Orbitron',system-ui;font-weight:900;letter-spacing:4px;line-height:1;font-size:24px;display:block;background:$authLogoGrad;-webkit-background-clip:text;-webkit-text-fill-color:transparent"
            })
            appendChild(div {
                style.cssText = "display:inline-block;margin-top:3px"
                appendChild(div {
                    style.cssText = "height:1px;background:$authLineGrad;opacity:0.3;border-radius:1px;margin-bottom:2px"
                })
                appendChild(div {
                    style.cssText = "font-size:5.5px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:${Styles.TEXT_30}"
                    textContent = "Health & Activity"
                })
                appendChild(div {
                    style.cssText = "font-size:5.5px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:${Styles.TEXT_30}"
                    textContent = "Planner & Tracker"
                })
            })
        })

        // Form card
        val formBg = if (ThemeManager.isDark()) "background:rgba(255,255,255,0.05);border:1.5px solid ${Styles.CARD_BORDER}" else "background:#f5f5fa;border:1.5px solid ${Styles.CARD_BORDER}"
        val form = el("form") {
            style.cssText = "$formBg;border-radius:20px;padding:28px 24px"
        }

        val linkColor = if (ThemeManager.isDark()) "#818cf8" else "#4f46e5"

        fun rebuildForm() {
            form.clear()

            // Title
            form.appendChild(div {
                style.cssText = "font-size:20px;font-weight:800;margin-bottom:24px;text-align:center;color:${Styles.TEXT}"
                textContent = when (mode) {
                    "forgot" -> t("auth_reset_password")
                    "signup" -> t("auth_signup")
                    else -> t("auth_login")
                }
            })

            // Email
            form.appendChild(div {
                style.cssText = "margin-bottom:14px"
                appendChild(label(t("auth_email")) { style.cssText = LABEL_STYLE })
                appendChild(input("email") {
                    style.cssText = INPUT_STYLE
                    placeholder = "you@example.com"
                    value = email
                    oninput = { e -> email = (e.target as HTMLInputElement).value; Unit }
                })
            })

            // Password
            if (mode != "forgot") {
                form.appendChild(div {
                    style.cssText = "margin-bottom:14px"
                    appendChild(label(t("auth_password")) { style.cssText = LABEL_STYLE })
                    appendChild(input("password") {
                        style.cssText = INPUT_STYLE
                        placeholder = "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
                        value = password
                        oninput = { e -> password = (e.target as HTMLInputElement).value; Unit }
                    })
                })
            }

            // Confirm password
            if (mode == "signup") {
                form.appendChild(div {
                    style.cssText = "margin-bottom:14px"
                    appendChild(label(t("auth_password_confirm")) { style.cssText = LABEL_STYLE })
                    appendChild(input("password") {
                        style.cssText = INPUT_STYLE
                        placeholder = "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
                        value = password2
                        oninput = { e -> password2 = (e.target as HTMLInputElement).value; Unit }
                    })
                })
            }

            // Forgot password link
            if (mode == "login") {
                form.appendChild(div {
                    style.cssText = "text-align:right;margin-bottom:16px;margin-top:-6px"
                    appendChild(button(t("auth_forgot_password")) {
                        type = "button"
                        style.cssText = "background:none;border:none;color:${Styles.TEXT_40};font-size:12px;font-weight:500;cursor:pointer;font-family:inherit"
                        onclick = { mode = "forgot"; error = ""; success = ""; rebuildForm() }
                    })
                })
            }

            // Error
            if (error.isNotBlank()) {
                form.appendChild(div {
                    style.cssText = "padding:10px 14px;border-radius:10px;background:#fb718515;color:#fb7185;font-size:13px;font-weight:600;margin-bottom:14px"
                    textContent = error
                })
            }

            // Success
            if (success.isNotBlank()) {
                form.appendChild(div {
                    style.cssText = "padding:14px;border-radius:12px;background:${Styles.A1}08;border:1.5px solid ${Styles.A1}20;margin-bottom:14px;text-align:center"
                    appendChild(div {
                        style.cssText = "color:${Styles.A1};font-size:14px;font-weight:700;margin-bottom:10px"
                        textContent = success
                    })
                    appendChild(button(t("auth_back_to_login")) {
                        type = "button"
                        style.cssText = "padding:8px 16px;border-radius:8px;border:none;background:${Styles.A1}15;color:${Styles.A1};font-size:12px;font-weight:700;cursor:pointer;font-family:inherit"
                        onclick = { mode = "login"; success = ""; rebuildForm() }
                    })
                })
            }

            // Submit button
            if (success.isBlank()) {
                val btnText = when (mode) {
                    "forgot" -> t("auth_reset_password")
                    "signup" -> t("auth_signup")
                    else -> t("auth_login")
                }
                val disabledStyle = if (ThemeManager.isDark()) {
                    "background:rgba(255,255,255,0.06);color:rgba(255,255,255,0.3)"
                } else {
                    "background:rgba(0,0,0,0.06);color:rgba(0,0,0,0.25)"
                }
                form.appendChild(button(btnText) {
                    type = "submit"
                    style.cssText = if (loading)
                        "width:100%;padding:15px;border-radius:12px;border:none;$disabledStyle;font-size:15px;font-weight:800;cursor:default;font-family:inherit;letter-spacing:0.5px"
                    else
                        "width:100%;padding:15px;border-radius:12px;border:none;${Styles.BTN_ACTION};font-size:15px;font-weight:800;cursor:pointer;font-family:inherit;letter-spacing:0.5px;transition:opacity 0.2s"
                    disabled = loading
                })
            }

            // Toggle login/signup
            form.appendChild(div {
                style.cssText = "text-align:center;margin-top:20px;padding-top:16px;border-top:1px solid ${Styles.SEPARATOR}"
                if (mode == "forgot") {
                    appendChild(button(t("auth_back_to_login")) {
                        type = "button"
                        style.cssText = "background:none;border:none;color:${Styles.TEXT_SEC};font-size:13px;font-weight:600;cursor:pointer;font-family:inherit"
                        onclick = { mode = "login"; error = ""; success = ""; rebuildForm() }
                    })
                } else {
                    appendChild(span(if (mode == "login") t("auth_no_account") + " " else t("auth_has_account") + " ") {
                        style.cssText = "color:${Styles.TEXT_SEC};font-size:13px"
                    })
                    appendChild(button(if (mode == "login") t("auth_signup") else t("auth_login")) {
                        type = "button"
                        style.cssText = "background:none;border:none;color:$linkColor;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit"
                        onclick = { mode = if (mode == "login") "signup" else "login"; error = ""; success = ""; rebuildForm() }
                    })
                }
            })
        }

        form.addEventListener("submit", { e: Event ->
            e.preventDefault()
            error = ""; success = ""

            if (email.isBlank() || !email.contains("@")) {
                error = t("auth_error_email"); rebuildForm(); return@addEventListener
            }

            if (mode == "forgot") {
                loading = true; rebuildForm()
                GlobalScope.launch {
                    try {
                        val res = Supa.resetPassword(email)
                        if (res.error != null && res.error != undefined) {
                            error = res.error.message?.toString() ?: "Error"
                        } else {
                            success = t("auth_reset_sent")
                        }
                    } catch (ex: Exception) {
                        error = ex.message ?: "Error"
                    }
                    loading = false; rebuildForm()
                }
                return@addEventListener
            }

            if (password.length < 6) {
                error = t("auth_error_password"); rebuildForm(); return@addEventListener
            }
            if (mode == "signup" && password != password2) {
                error = t("auth_error_match"); rebuildForm(); return@addEventListener
            }

            loading = true; rebuildForm()
            GlobalScope.launch {
                try {
                    if (mode == "login") {
                        val res = Supa.signIn(email, password)
                        if (res.error != null && res.error != undefined) {
                            error = res.error.message?.toString() ?: "Error"
                        } else {
                            onAuth?.invoke(res.data.session)
                        }
                    } else {
                        val res = Supa.signUp(email, password)
                        if (res.error != null && res.error != undefined) {
                            error = res.error.message?.toString() ?: "Error"
                        } else if (res.data.session != null && res.data.session != undefined) {
                            onAuth?.invoke(res.data.session)
                        } else {
                            success = t("auth_check_email")
                        }
                    }
                } catch (ex: Exception) {
                    error = ex.message ?: "Error"
                }
                loading = false; rebuildForm()
            }
        })

        rebuildForm()
        wrapper.appendChild(form)
        container.appendChild(wrapper)
        return container
    }
}
